import Router from 'vue-router';
import {NAVI_SERVICE_INSTANCE} from '@/services/navi.service';
import store from '@/store';
import {DisguiseRoute} from '@/routetype';
import WelcomeTemplate from '@/views/WelcomeTemplate.vue';
import LoginTemplate from '@/views/LoginTemplate.vue';
import LogoutTemplate from '@/views/LogoutTemplate.vue';
import FormTester from '@/views/formular/formtester/form-tester.vue';
import FieldTesterTemplate from '@/views/FieldTesterTemplate.vue';
import {ErrorHandler, Location, RawLocation, Route} from 'vue-router/types/router';
import HolidayGeneratorTemplate from '@/views/HolidayGeneratorTemplate.vue';
import TokenLoginTemplate from '@/views/TokenLoginTemplate.vue';
// import GMAP from '@/views/GMAP.vue';

export const WELCOME_ROUTE: DisguiseRoute = {
    path: '/welcome',
    name: 'WelcomeTemplate',
    component: WelcomeTemplate
};

export const LOGIN_ROUTE: DisguiseRoute = {
    path: '/login',
    name: 'LoginTemplate',
    component: LoginTemplate,
    meta: {
        public: true,
        hideNavigation: true
    }
};

export const TOKEN_LOGIN_ROUTE: DisguiseRoute = {
    path: '/tokenlogin',
    name: 'tokenLoginTemplate',
    component: TokenLoginTemplate,
    meta: {
        public: true,
        hideNavigation: true
    }
};

export const LOGOUT_ROUTE: DisguiseRoute = {
    path: '/logout',
    name: 'LogoutTemplate',
    component: LogoutTemplate,
    meta: {
        naviText: 'Logout',
        naviIcon: 'logout',
        depth: 0,
        showInFooterSection: true
    }
};

export const FORMULAR_TESTER_ROUTE: DisguiseRoute = {
    path: '/formtester',
    name: 'FormTester',
    component: FormTester,
    meta: {
        naviText: 'FormTester',
        depth: 0,
        showInFooterSection: true
    }
};

export const FIELD_TESTER_ROUTE: DisguiseRoute = {
    path: '/fieldtester',
    name: 'Fieldtester',
    component: FieldTesterTemplate
};

export const HOLIDAY_GENERATOR_ROUTE: DisguiseRoute = {
    path: '/holidaygenerator',
    name: 'Holidaygenerator',
    component: HolidayGeneratorTemplate
};

// export const GMAP_ROUTE: DisguiseRoute = {
//     path: '/mapstest',
//     name: 'mapstest',
//     component: GMAP
// };

class RouterService {

    private static readonly PUBLIC_ROUTES = [
        LOGIN_ROUTE,
        WELCOME_ROUTE,
        LOGOUT_ROUTE,
        FIELD_TESTER_ROUTE,
        TOKEN_LOGIN_ROUTE,
        // FORMULAR_TESTER_ROUTE,
        // HOLIDAY_GENERATOR_ROUTE,
        // GMAP_ROUTE
    ];

    private protectedRoutesInitialized: boolean = false;
    private router: Router | undefined;

    getInstance() {
        if (!this.router) {
            this.protectedRoutesInitialized = false;
            this.router = this.createNewRouterInstance();

            const router: Router = this.router;

            router.onReady(() => {
                /**
                 * This method block creates an interceptor
                 *
                 * the router internally changes the url using html5history.push
                 * the html5history.push mechanism will NOT load the url (or trigger any events) if it is the same as
                 * currently opened.
                 *
                 * in order to intercept calls to the router before the html5history.push is called, we overwrite the
                 * push method of the router:
                 * - if it is the same url we want to open, we use our internal mechanism to refresh the page.
                 * - if it is any other url, we use the original router mechanism to change the url.
                 */
                const originalRouterPushFn: (location: RawLocation,
                                             onComplete?: (() => void),
                                             onAbort?: ErrorHandler) => Promise<Route> | void = router.push;
                // @ts-ignore
                router.push = function(
                  location: RawLocation,
                  onComplete?: (() => void),
                  onAbort?: ErrorHandler
                ): Promise<Route> | void {
                    let target: string | undefined;
                    if (typeof location === 'string') {
                        // location can be either string
                        target = location;
                    } else {
                        // location can also be an object
                        target = (location as Location).path;
                    }
                    if (target === router.currentRoute.fullPath) {
                        // our internal mechanism to re-render the same page
                        store.commit('rerenderRouterView');
                    } else {
                        // call the original method
                        const result = originalRouterPushFn.apply(this, [location, onComplete, onAbort]);
                        if (result) {
                            return result;
                        }
                    }
                };
            });

            // register lifecycle hooks
            router.beforeEach((to, from, next) => {
                if ((to.matched.length === 0 || !(to.meta || {}).public) && !this.protectedRoutesInitialized) {
                    this.protectedRoutesInitialized = true;
                    NAVI_SERVICE_INSTANCE.loadNaviCached().subscribe({
                        next: (rawNaviResponse) => {
                            const naviResponseCopy = [...rawNaviResponse.navi || []];
                            const superUser = rawNaviResponse.superUser === true;
                            const naviSearchEnabled = rawNaviResponse.naviSearchEnabled === true;
                            const documentationEnabled = rawNaviResponse.documentationEnabled === true;
                            const convertedDisguiseRoutes =
                              NAVI_SERVICE_INSTANCE.convertNaviEntriesToDisguiseRoutes(naviResponseCopy);
                            router.addRoutes(convertedDisguiseRoutes);
                            for (const currentPublicRoute of RouterService.PUBLIC_ROUTES) {
                                if (currentPublicRoute.meta && currentPublicRoute.meta.naviText && naviResponseCopy) {
                                    const showInFooterSection = currentPublicRoute.meta
                                      && currentPublicRoute.meta.showInFooterSection === true;
                                    naviResponseCopy.push({
                                        canBeFavorite: false,
                                        depth: 0,
                                        name: currentPublicRoute.meta.naviText,
                                        icon: currentPublicRoute.meta.naviIcon,
                                        fullPath: currentPublicRoute.path,
                                        showInFooterSection,
                                        isFavorite: false
                                    });
                                }
                            }
                            store.commit('setNavi', {navi: naviResponseCopy, superUser, naviSearchEnabled, documentationEnabled});
                            if (naviSearchEnabled) {
                                NAVI_SERVICE_INSTANCE.loadAllSubmenuEntries().then((response) => {
                                    store.commit('setSubmenuEntries', {entries: response.data || []});
                                }).catch(() => { /* Submenu-Cache nicht verfügbar */ });
                            }
                            next(to.fullPath);
                        },
                        error: (error) => {
                            let statusMessage;
                            switch (error.response.status) {
                                case 503:
                                    statusMessage = 'Navigationsdienst ist nicht erreichbar';
                                    break;
                                default:
                                    statusMessage = 'Fehler beim Abruf der Navigation';
                                    break;
                            }
                            store.commit('showSnack', {
                                message: statusMessage,
                                color: 'error'
                            });
                            this.protectedRoutesInitialized = false;
                            next(false);
                        }
                    });
                } else {
                    next();
                }
            });
        }
        return this.router;
    }

    /**
     * Resets the existing and already initialized routes
     */
    resetProtectedRoutes() {
        // see https://github.com/vuejs/vue-router/issues/1234#issuecomment-573644920
        (this.router as any).matcher = (this.createNewRouterInstance() as any).matcher;
        this.protectedRoutesInitialized = false;
    }

    private createNewRouterInstance() {
        return new Router({
            mode: 'history',
            base: process.env.BASE_URL,
            routes: RouterService.PUBLIC_ROUTES
        });
    }
}

const ROUTER_SERVICE = new RouterService();

export {ROUTER_SERVICE};
